import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { meetingsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Calendar, Clock, Users, Video, Lock, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const TOPIC_LABELS = {
  'alcool': 'Álcool',
  'nicotina': 'Nicotina',
  'drogas': 'Drogas',
  'cocaina': 'Cocaína',
  'emocoes': 'Emoções',
  'sexo-afetividade': 'Sexo e Afetividade',
  'jogos-apostas': 'Jogos e Apostas',
  'lgbt': 'LGBT+',
  'comedores-compulsivos': 'Comedores Compulsivos',
  'familiares': 'Familiares',
  'prevencao-recaida': 'Prevenção de Recaída',
  'geral': 'Geral',
};

const STATUS_LABELS = {
  'scheduled': 'Agendada',
  'live': 'Ao Vivo',
  'ended': 'Encerrada',
  'cancelled': 'Cancelada',
};

const STATUS_COLORS = {
  'scheduled': 'bg-blue-100 text-blue-800',
  'live': 'bg-green-100 text-green-800 animate-pulse',
  'ended': 'bg-gray-100 text-gray-800',
  'cancelled': 'bg-red-100 text-red-800',
};

export function MeetingDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    loadMeeting();
  }, [slug]);

  const loadMeeting = async () => {
    setLoading(true);
    try {
      const response = await meetingsAPI.getMeeting(slug);
      setMeeting(response.data);
    } catch (error) {
      console.error('Failed to load meeting:', error);
      if (error.response?.status === 403) {
        toast.error('Você não tem permissão para acessar esta sala');
      } else if (error.response?.status === 404) {
        toast.error('Sala não encontrada');
      } else {
        toast.error('Erro ao carregar sala');
      }
      navigate('/reunioes');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!meeting) return;

    setJoining(true);
    try {
      // Join meeting first to create participant record
      await meetingsAPI.joinMeeting(meeting.id);
      toast.success('Entrando na sala...');
      
      // Redirect to room
      navigate(`/reunioes/${slug}/room`);
    } catch (error) {
      console.error('Failed to join meeting:', error);
      toast.error(error.response?.data?.detail || 'Erro ao entrar na sala');
    } finally {
      setJoining(false);
    }
  };

  const formatDateTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, "EEEE, dd 'de' MMMM 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const canJoin = meeting?.status === 'live' && 
    (meeting.visibility === 'public' || meeting.is_host || meeting.is_moderator || meeting.is_participant);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-6 px-4">
        <Card className="mb-6">
          <CardHeader>
            <Skeleton className="h-8 w-3/4 mb-2" />
            <Skeleton className="h-4 w-1/2" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-20 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!meeting) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4" data-testid="meeting-detail-page">
      {/* Header Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-start justify-between gap-4 mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge variant="secondary" className="text-sm">
                  {TOPIC_LABELS[meeting.topic] || meeting.topic}
                </Badge>
                <Badge variant="secondary" className={STATUS_COLORS[meeting.status]}>
                  {meeting.status === 'live' && <Video className="h-3 w-3 mr-1" />}
                  {STATUS_LABELS[meeting.status]}
                </Badge>
                {meeting.visibility === 'private' && (
                  <Badge variant="outline">
                    <Lock className="h-3 w-3 mr-1" />
                    Privada
                  </Badge>
                )}
              </div>
              <CardTitle className="text-2xl" data-testid="meeting-detail-title">
                {meeting.title}
              </CardTitle>
            </div>

            {/* Main CTA */}
            <div>
              {meeting.status === 'live' && canJoin && (
                <Button
                  size="lg"
                  onClick={handleJoin}
                  disabled={joining}
                  data-testid="join-meeting-button"
                  className="bg-green-600 hover:bg-green-700"
                >
                  {joining ? (
                    'Entrando...'
                  ) : (
                    <>
                      <Video className="h-4 w-4 mr-2" />
                      Entrar Agora
                    </>
                  )}
                </Button>
              )}
              {meeting.status === 'scheduled' && (
                <Badge variant="secondary" className="text-sm px-4 py-2">
                  <Calendar className="h-4 w-4 mr-2" />
                  Agendada
                </Badge>
              )}
              {meeting.status === 'ended' && (
                <Badge variant="secondary" className="text-sm px-4 py-2 bg-gray-200">
                  Encerrada
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Description */}
          <p className="text-muted-foreground" data-testid="meeting-description">
            {meeting.description}
          </p>

          <Separator />

          {/* Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <p className="font-medium">Data e Hora</p>
                <p className="text-muted-foreground">{formatDateTime(meeting.start_at)}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <p className="font-medium">Duração</p>
                <p className="text-muted-foreground">{meeting.duration_min} minutos</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Users className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <p className="font-medium">Participantes</p>
                <p className="text-muted-foreground">
                  {meeting.participant_count}/{meeting.capacity_max}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Rules */}
          {meeting.rules && meeting.rules.length > 0 && (
            <Accordion type="single" collapsible>
              <AccordionItem value="rules">
                <AccordionTrigger data-testid="rules-toggle">
                  Regras da Sala ({meeting.rules.length})
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2" data-testid="meeting-rules">
                    {meeting.rules.map((rule, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                        <span className="text-sm">{rule}</span>
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          {/* Private Room Message */}
          {meeting.visibility === 'private' && !meeting.is_host && !meeting.is_moderator && !meeting.is_participant && (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-900 mb-1">Sala Privada</h4>
                    <p className="text-sm text-amber-800">
                      Esta é uma sala privada. Você precisa de um convite para participar.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Participant Status */}
          {meeting.is_host && (
            <Badge variant="secondary" className="bg-purple-100 text-purple-800">
              Você é host desta sala
            </Badge>
          )}
          {meeting.is_moderator && (
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
              Você é moderador desta sala
            </Badge>
          )}
          {meeting.is_participant && (
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              Você já está participando desta sala
            </Badge>
          )}
        </CardContent>
      </Card>

      {/* Back Button */}
      <Button
        variant="outline"
        onClick={() => navigate('/reunioes')}
        data-testid="back-button"
      >
        ← Voltar para Salas
      </Button>
    </div>
  );
}
